import { useState } from 'react';
import { 
  ArrowLeft, 
  Clock, 
  MapPin, 
  DollarSign, 
  Star, 
  Shield, 
  Zap, 
  CheckCircle,
  Edit,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type { JobDetailsProps, BroUser } from './types';
import ApplicationForm from './Applications/ApplicationForm';
import JobBoardAccessGate from './JobBoardAccessGate';
import { useJobBoardAccess } from '@/hooks/useJobBoardAccess';

const JobDetails = ({ job, onBack, onEdit, canApply = false, isOwner = false, user }: JobDetailsProps & { user?: BroUser }) => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'applications' | 'activity'>('details');
  const { access } = useJobBoardAccess(user || null);

  const formatBudget = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return 'Budget not specified';
    
    const currencySymbol = currency === 'BRO_SCORE' ? 'BroScore' : currency === 'TOP1_TOKEN' ? '$TOP1' : '$';
    
    if (min && max) {
      return `${min}-${max} ${currencySymbol}`;
    }
    if (min) {
      return `${min}+ ${currencySymbol}`;
    }
    if (max) {
      return `Up to ${max} ${currencySymbol}`;
    }
    return 'Budget negotiable';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'assigned': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'completed': return 'bg-green-600';
      case 'cancelled': return 'bg-gray-500';
      case 'disputed': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getExperienceBadgeColor = (level: string) => {
    switch (level) {
      case 'entry': return 'bg-green-500';
      case 'intermediate': return 'bg-yellow-500';
      case 'expert': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Zap className="text-red-400" size={16} />;
      case 'high': return <Star className="text-yellow-400" size={16} />;
      default: return null;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    }
    if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    }
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  if (showApplicationForm && canApply) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowApplicationForm(false)}
            className="text-tan hover:text-tan-dark"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-bold text-tan">Apply for {job.title}</h2>
        </div>
        <ApplicationForm 
          job={job}
          onSubmit={() => setShowApplicationForm(false)}
          onCancel={() => setShowApplicationForm(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-tan hover:text-tan-dark"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-tan">Job Details</h1>
      </div>

      {/* Job Header */}
      <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {job.featured && (
                <Star className="text-yellow-400 fill-current" size={20} />
              )}
              <h2 className="text-2xl font-bold text-tan">{job.title}</h2>
              {getPriorityIcon(job.priority)}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-tan-dark mb-2">
              <span className="flex items-center gap-1">
                <Clock size={14} />
                Posted {formatTimeAgo(job.created_at)}
              </span>
              {job.category && (
                <span className="flex items-center gap-1">
                  <span>{job.category.icon}</span>
                  {job.category.name}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <Badge className={`${getStatusColor(job.status)} text-white`}>
              {job.status.replace('_', ' ').toUpperCase()}
            </Badge>
            <Badge className={`${getExperienceBadgeColor(job.experience_level)} text-white`}>
              {job.experience_level}
            </Badge>
          </div>
        </div>

        {/* Budget */}
        <div className="bg-navy rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="text-tan" size={20} />
              <span className="text-tan font-bold text-lg">
                {formatBudget(job.budget_min, job.budget_max, job.budget_currency)}
              </span>
            </div>
            <Badge variant="outline" className="border-tan-dark text-tan-dark">
              {job.payment_type}
            </Badge>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {canApply && job.status === 'open' && (
            <>
              {access.canApplyToJobs ? (
                <Button
                  onClick={() => setShowApplicationForm(true)}
                  className="bg-tan text-navy hover:bg-tan-dark flex-1"
                >
                  Apply for this Job
                </Button>
              ) : (
                <JobBoardAccessGate
                  user={user || null}
                  requiredLevel="apply"
                  onVerificationComplete={() => window.location.reload()}
                >
                  <Button
                    onClick={() => setShowApplicationForm(true)}
                    className="bg-tan text-navy hover:bg-tan-dark flex-1"
                  >
                    Apply for this Job
                  </Button>
                </JobBoardAccessGate>
              )}
            </>
          )}
          
          {isOwner && (
            <>
              <Button
                variant="outline"
                onClick={() => onEdit?.(job)}
                className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
              >
                <Edit size={16} className="mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <Trash2 size={16} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Requirements Alert */}
      {(job.min_bro_score > 0 || job.requires_verification) && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <strong>Requirements:</strong>
            {job.min_bro_score > 0 && ` Minimum ${job.min_bro_score} BroScore required.`}
            {job.requires_verification && ` Telegram verification required.`}
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-navy-light rounded-lg p-1 border border-tan-dark">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'details'
              ? 'bg-tan text-navy'
              : 'text-tan-dark hover:text-tan'
          }`}
        >
          Details
        </button>
        {isOwner && (
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'applications'
                ? 'bg-tan text-navy'
                : 'text-tan-dark hover:text-tan'
            }`}
          >
            Applications ({job.applications_count || 0})
          </button>
        )}
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'activity'
              ? 'bg-tan text-navy'
              : 'text-tan-dark hover:text-tan'
          }`}
        >
          Activity
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          {/* Description */}
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <h3 className="text-lg font-bold text-tan mb-3">Job Description</h3>
            <p className="text-tan-dark leading-relaxed whitespace-pre-wrap">
              {job.description}
            </p>
          </div>

          {/* Skills & Requirements */}
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <h3 className="text-lg font-bold text-tan mb-3">Required Skills</h3>
            <div className="flex flex-wrap gap-2">
              {job.required_skills.map((skill, index) => (
                <Badge key={index} variant="outline" className="border-tan text-tan">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>

          {/* Job Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
              <h3 className="text-lg font-bold text-tan mb-3">Job Information</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-tan-dark">Duration:</span>
                  <span className="text-tan">{job.estimated_duration || 'Not specified'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-tan-dark">Remote:</span>
                  <span className="text-tan">{job.remote_allowed ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-tan-dark">Auto-assign:</span>
                  <span className="text-tan">{job.auto_assign ? 'Yes' : 'No'}</span>
                </div>
                {job.max_applications && (
                  <div className="flex items-center justify-between">
                    <span className="text-tan-dark">Max Applications:</span>
                    <span className="text-tan">{job.max_applications}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
              <h3 className="text-lg font-bold text-tan mb-3">Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-tan-dark">Posted:</span>
                  <span className="text-tan">{new Date(job.created_at).toLocaleDateString()}</span>
                </div>
                {job.deadline && (
                  <div className="flex items-center justify-between">
                    <span className="text-tan-dark">Deadline:</span>
                    <span className="text-tan">{new Date(job.deadline).toLocaleDateString()}</span>
                  </div>
                )}
                {job.expires_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-tan-dark">Expires:</span>
                    <span className="text-tan">{new Date(job.expires_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {job.tags.length > 0 && (
            <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
              <h3 className="text-lg font-bold text-tan mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {job.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-navy rounded px-2 py-1 text-sm text-tan-dark border border-tan-dark"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Location */}
          {job.location && (
            <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
              <h3 className="text-lg font-bold text-tan mb-3">Location</h3>
              <div className="flex items-center gap-2 text-tan-dark">
                <MapPin size={16} />
                <span>{job.location}</span>
                {job.timezone && <span>({job.timezone})</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'applications' && isOwner && (
        <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
          <h3 className="text-lg font-bold text-tan mb-3">Applications</h3>
          <p className="text-tan-dark">Applications management will be implemented here.</p>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
          <h3 className="text-lg font-bold text-tan mb-3">Activity Timeline</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-navy rounded border border-tan-dark">
              <CheckCircle className="text-green-400" size={16} />
              <div>
                <p className="text-tan text-sm font-medium">Job posted</p>
                <p className="text-tan-dark text-xs">{formatTimeAgo(job.created_at)}</p>
              </div>
            </div>
            {/* More activity items would be added here */}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetails;